/**
 * DesignerWorkspace — the 3D designer itself, independent of where it is shown.
 *
 * Renders a room's positioned sections (`RoomSurfaces`) in the 3D viewer with
 * edit mode on. Click a wall or the ceiling to turn to it and lock; then click
 * a section to select it, drag it or its handles, set its product, colour,
 * board direction and exact size, duplicate or delete it, or add a new one on
 * bare wall. Windows, doors and garage doors are added from the panel at the
 * top left and worked on the same way. Shift-click selects several things, to
 * align or distribute together; click an edge of a lone selection to choose its
 * trim. A live readout shows the materials the layout on screen needs.
 *
 * It is CONTROLLED: the room comes in as a prop and every edit goes out through
 * `onCommit` — and only edits the placement rules allow (see
 * `calculations/sections/edits` and `openings`); a refused edit shows its
 * reason and changes nothing. Hosts decide where the room lives and own undo /
 * redo: the modal (`Designer3D`) and the sandbox page (`DesignerSandbox`) keep
 * it in local state today; the estimator's own state can supply it later
 * without changes here.
 *
 * Who owns what:
 *   viewer     — camera, lock animation, what is drawn
 *   workspace  — which surface is locked, what is selected
 *   overlay    — the drag in progress
 *   host       — the room and its history
 *
 * Fills its parent: the host provides the box (full-window in both hosts).
 */

import type { Yard } from '../../calculations/plan/yard';
import { panoramaFace } from '../ui/room3d/panorama';
import { loadPhotoRender } from '../ui/room3d/photoRender';
import { applyFinish } from '../ui/room3d/renderFinish';
import type { PlanFinish } from '../../calculations/plan/floorPlan';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppWindow, ChevronDown, ChevronLeft, ChevronRight, DoorClosed, Redo2, Trash2, Undo2, Warehouse, X } from 'lucide-react';
import { renderExportFiles, RenderManifest } from '../ui/room3d/sceneExport';
import { mostFinishedSideWall, oppositeWall } from '../ui/room3d/renderPose';
import { DesignerHeader, DesignerBrand } from './chrome/DesignerHeader';
import { IconTextButton } from './chrome/IconTextButton';
import { usePhoneSized } from './chrome/usePhoneSized';
import { Room3DViewer } from '../ui/room3d';
import { WallFace, SurfaceFace, SurfaceProjection, Room3DViewerApi, ContextRoom, FramingOmit, isCeilingFace } from '../ui/room3d/types';
import { CeilingProfile, ceilingGeometry } from '../../calculations/ceiling/profile';
import type { StagedPropView } from '../ui/room3d/stagingMeshes';
import { roomSurfacesToLayouts } from '../ui/room3d/sectionLayoutAdapter';
import { SectionEditOverlay, EDGE_COLORS } from './SectionEditOverlay';
import { SectionToolbar } from './SectionToolbar';
import { OpeningToolbar } from './OpeningToolbar';
import { RectField, RectPatch } from './RectFields';
import { ArrangeControl, ArrangeSettings, DEFAULT_ARRANGE } from './ArrangePopover';
import { CopyLayoutPopover } from './CopyLayoutPopover';
import { copyLayout } from '../../calculations/sections/copyLayout';
import { EdgeTrimMenu, EdgeTrimOption } from './EdgeTrimMenu';
import { QuickLayoutMenu, QuickLayoutChoice } from './QuickLayoutMenu';
import { AccessoryPalette } from './AccessoryPalette';
import { SlatWallAccessoryKind, accessoryDef } from '../../data/slatwallAccessories';
import type { AccessoryView } from '../ui/room3d/types';
import {
  Rect,
  EditResult,
  AlignMode,
  DistributeSpacing,
  OpeningRules,
  addSection,
  addSections,
  addAccessory,
  moveAccessory,
  nudgeAccessory,
  deleteAccessory,
  findAccessory,
  arrangeAccessories,
  accessoryRect,
  RectPatches,
  QUICK_LAYOUTS,
  QuickLayoutId,
  quickLayoutHeights,
  quickLayoutLevelHeight,
  quickLayoutSections,
  duplicateSection,
  updateSection,
  changeProductType,
  sectionForGap,
  addOpening,
  updateOpening,
  duplicateOpening,
  updateItems,
  nudgeItems,
  setOpeningFloorPinned,
  deleteItems,
  alignPatches,
  distributePatches,
  boundsOf,
  calculateFromSurfaces,
  SurfacesEstimate,
  sectionTrims,
  openingTrims,
  availableTrims,
  findTrimProduct,
  TRIM_NONE,
} from '../../calculations/sections';
import { RoomSurfaces, Surface, Section, SectionProductType, EdgeSide, OpeningKind } from '../../types/sections';
import { MaterialItem, ProductColor, RoomDimensions } from '../../types';
import { useAppSettings } from '../../context/AppSettingsContext';
import { DesignerUiState } from './sandboxStorage';
import { getAvailableProductTypes, pickColor } from './sectionOptions';
import { DESIGNER_STRINGS as S } from './strings';
import { RenderControls } from './render/RenderControls';
import { ViewLogo } from './render/ViewLogo';

type Face = SurfaceFace;

// Viewer box order is [front, right, back, left] (see sectionLayoutAdapter).
const FACE_INDEX: Record<WallFace, number> = { front: 0, right: 1, back: 2, left: 3 };
/** Order the previous / next buttons walk the room in: the walls, then the ceiling (or each of its planes). */
function faceOrder(room: RoomSurfaces): Face[] {
  const walls: Face[] = ['front', 'right', 'back', 'left'];
  if (room.ceiling) return [...walls, 'ceiling'];
  return [...walls, ...Object.keys(room.ceilingPlanes ?? {}) as Face[]];
}

function surfaceForFace(room: RoomSurfaces, face: Face): Surface | undefined {
  if (face === 'ceiling') return room.ceiling;
  if (isCeilingFace(face)) return room.ceilingPlanes?.[face];
  return room.walls[FACE_INDEX[face]];
}

const totalQty = (items: MaterialItem[]) => items.reduce((sum, i) => sum + i.quantity, 0);
const rectOf = (s: { x: number; y: number; width: number; height: number }): Rect => ({ x: s.x, y: s.y, width: s.width, height: s.height });

export interface DesignerWorkspaceProps {
  dims: RoomDimensions;
  room: RoomSurfaces;
  /**
   * Called with the whole new room after each edit. `coalesceKey` marks edits
   * that belong together (stepping one number field) so the host can fold them
   * into a single undo step.
   */
  onCommit: (room: RoomSurfaces, coalesceKey?: string) => void | string;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  /** Lock / selection to restore on mount (e.g. after a reload). */
  initialUi?: DesignerUiState;
  /** Reports lock / selection changes so the host can persist them. */
  onUiChange?: (ui: DesignerUiState) => void;
  /** When given, a close button is shown and Escape (with nothing selected or locked) closes. */
  onClose?: () => void;
  title?: string;
  /** The header's logo (a retailer's, say). Trusscore's when absent. */
  brand?: DesignerBrand;
  /** Extra header content, rendered between the title and the render controls. */
  headerSlot?: React.ReactNode;
  /** Header content for the far right, after the render controls. */
  headerEnd?: React.ReactNode;
  /**
   * The materials to show. Given by a host whose document is more than this
   * one room (a floor plan); otherwise the room's own are worked out here.
   */
  estimate?: SurfacesEstimate;
  /**
   * Floor plans. `room` is the FOCUS room; these describe it within the plan
   * and the rooms around it, which the 3D view draws in place.
   */
  roomKey?: string;
  /** Changes when the host opens another document in place: the lock, selection and menus are dropped. */
  documentKey?: number;
  /** The focus room's front-left corner on the plan (feet). */
  roomOrigin?: { x: number; z: number };
  /** Stretches of the focus room's framing that a neighbour's wall stands in for. */
  framingOmit?: Partial<Record<WallFace, FramingOmit[]>>;
  /** Staged props in the room shown. */
  /** The plan's space type: which wall the photoreal 360 opens on (panorama.ts). */
  spaceType?: string;
  props?: StagedPropView[];
  /** The yard round the house (calculations/plan/yard.ts), sheet coordinates. */
  yard?: Yard;
  /** The garage doors the staging holds open (a car part way out through them): the viewer's opener keys. */
  heldOpen?: string[];
  /** What Stage drew for the render (a floor set, an HDRI): fills what the photoreal settings leave open at export. */
  finish?: PlanFinish;
  /** The SlatWall accessories the user hung in the room shown, for the 3D view. */
  accessoryViews?: AccessoryView[];
  /** The user's accessories, counted (a line in the readout). */
  accessories?: { sku: string; label: string; quantity: number }[];
  otherRooms?: WorkspaceOtherRoom[];
  /** The shape of the focus room's ceiling (absent = flat at the wall height). */
  ceilingProfile?: CeilingProfile;
  /** Another room was clicked in the 3D view. */
  onFocusRoom?: (roomId: string) => void;
  /** A floor was double-clicked in the 3D view. */
  onOpenPlanner?: () => void;
  /**
   * Mounted but under the floor planner: the 3D viewer keeps its WebGL
   * context, programs and textures (so coming back is not a cold start), but
   * builds and draws nothing, shows no edit overlay and takes no keys.
   */
  suspended?: boolean;
  /** Set to the workspace's capture (a still + export of the room) while it is mounted. */
  captureRef?: React.MutableRefObject<DesignerCapture | null>;
}

/** A still of the room for the estimate, with its export for the render job (renderPose.ts frames it). */
export interface DesignerCapture {
  capture: (options: { width: number; height: number }) => Promise<{
    image: string;
    camera: RenderManifest['camera'];
    glb: ArrayBuffer | null;
    manifest: RenderManifest | null;
  } | null>;
}

/** One of the plan's other rooms, placed relative to the focus room. */
export interface WorkspaceOtherRoom {
  id: string;
  offsetX: number;
  offsetZ: number;
  dims: RoomDimensions;
  surfaces: RoomSurfaces;
  /** The shape of its ceiling (absent = flat). */
  ceilingProfile?: CeilingProfile;
  framingOmit?: Partial<Record<WallFace, FramingOmit[]>>;
  /** Its staged props. */
  props?: StagedPropView[];
  /** The SlatWall accessories the user hung in it. */
  accessories?: AccessoryView[];
}

/**
 * Height (px) of the band at the top of the locked view that is kept clear of
 * the surface, for the floating selection panel (two rows of controls + margins).
 */
const EDIT_INSET_TOP = 140;
/** More of it when the accessory palette sits under the toolbar. */
const PALETTE_INSET = 110;
/** And at the bottom, for the surface's width dimension drawn under it. */
const EDIT_INSET_BOTTOM = 34;

const FLOATING_PANEL: React.CSSProperties = {
  position: 'absolute', zIndex: 4,
  background: 'rgba(26, 26, 30, 0.9)', backdropFilter: 'blur(10px)', color: '#fff',
  border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
  pointerEvents: 'auto', userSelect: 'none',
};

const headerButton = (enabled: boolean): React.CSSProperties => ({
  background: 'rgba(255,255,255,0.08)', color: '#fff',
  border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6,
  width: 30, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: enabled ? 'pointer' : 'default', opacity: enabled ? 1 : 0.35, padding: 0,
});

/** Arrow keys as screen directions: [right, up]. */
const NUDGE_ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1],
};
/** One nudge, in feet: 6" plain, 1" with Ctrl, 1' with Shift. */
const NUDGE_FT = 0.5;
const NUDGE_FINE_FT = 1 / 12;
const NUDGE_COARSE_FT = 1;

const ADD_OPENING: { kind: OpeningKind; Icon: typeof AppWindow }[] = [
  { kind: 'window', Icon: AppWindow },
  { kind: 'door', Icon: DoorClosed },
  { kind: 'garageDoor', Icon: Warehouse },
];

export const DesignerWorkspace: React.FC<DesignerWorkspaceProps> = ({
  dims,
  room,
  spaceType,
  finish,
  onCommit,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  initialUi,
  onUiChange,
  onClose,
  title = S.title,
  brand,
  headerSlot,
  headerEnd,
  estimate: hostEstimate,
  roomKey,
  documentKey,
  roomOrigin,
  framingOmit,
  props,
  yard,
  heldOpen,
  accessoryViews,
  accessories,
  otherRooms,
  ceilingProfile,
  onFocusRoom,
  onOpenPlanner,
  suspended = false,
  captureRef,
}) => {
  const [lockedFace, setLockedFace] = useState<Face | null>(null);
  // Selected sections / openings on the locked surface, in the order they were picked.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [arrangeOpen, setArrangeOpen] = useState(false);
  const [arrangeSettings, setArrangeSettings] = useState<ArrangeSettings>(DEFAULT_ARRANGE);
  // The edge of the selected item whose trim is being chosen, and where the menu sits (viewer px).
  const [edgeMenu, setEdgeMenu] = useState<{ id: string; side: EdgeSide; x: number; y: number } | null>(null);
  // The bare area whose "+" was clicked, and where its quick-layout menu sits (viewer px).
  const [addMenu, setAddMenu] = useState<{ gap: Rect; x: number; y: number } | null>(null);
  // The surface menu under the locked surface's name (go straight to any wall or the ceiling).
  const [surfaceMenuOpen, setSurfaceMenuOpen] = useState(false);
  useEffect(() => { setSurfaceMenuOpen(false); }, [lockedFace]);
  useEffect(() => {
    if (!surfaceMenuOpen) return;
    // A press anywhere outside the menu dismisses it.
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.('[role="menu"], .designer-surface-title')) setSurfaceMenuOpen(false); };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [surfaceMenuOpen]);
  const [projection, setProjection] = useState<SurfaceProjection | null>(null);
  // Tracks the viewer's Hide Framing button state so the overlay can draw the
  // wall perimeter line only when framing is hidden (studs otherwise indicate
  // the wall edges naturally).
  const [framingHidden, setFramingHidden] = useState(false);
  // Why the last edit was refused (cleared by the next successful edit or after a few seconds).
  const [notice, setNotice] = useState<string | null>(null);
  const viewerApi = useRef<Room3DViewerApi | null>(null);
  // A still of the room and its export, for the estimate (Get Estimate on
  // the Advanced page): framed on the room's feature wall unless the user is
  // inside the room, the same framing the photoreal "this view" gets. The
  // export is a bonus — if it fails (a phone short of memory) the still stands.
  useEffect(() => {
    if (!captureRef) return;
    captureRef.current = {
      capture: async ({ width, height }) => {
        const api = viewerApi.current;
        if (!api) return null;
        const feature = panoramaFace(spaceType, room, props);
        const secondary = mostFinishedSideWall(room, [feature, oppositeWall(feature)]);
        const pose = api.framing(feature, secondary);
        if (!pose) return null;
        const shot = await api.snapshot({ width, height, pose });
        if (!shot) return null;
        let out: { glb: ArrayBuffer; manifest: RenderManifest } | null = null;
        try { out = await api.exportScene({ panoramaFace: feature, camera: pose }); } catch { out = null; }
        // How Blender is to render it — the HDRI, lens, output — is this browser's
        // photo render settings (the Render window), as the sandbox's Render button sends them.
        return { image: shot.dataUrl, camera: shot.camera, glb: out?.glb ?? null, manifest: out ? { ...out.manifest, render: applyFinish(loadPhotoRender(), finish) } : null };
      },
    };
    return () => { captureRef.current = null; };
  });
  const phone = usePhoneSized();

  // The openings' safe margin is an admin setting, in inches.
  const { settings } = useAppSettings();
  const openingRules = useMemo<OpeningRules>(() => ({ margin: settings.openingSafeMarginIn / 12 }), [settings.openingSafeMarginIn]);

  // Restoring a lock (e.g. after a reload) is the viewer's job: it re-locks
  // `initialLockedFace` once its scene is up and reports it through onFaceLock.
  // Captured once at mount — the host's copy changes as we report UI state back.
  // While that restore is pending we don't report our (still empty) lock state,
  // or the host would overwrite the very state we're about to restore; and the
  // selection waits for the lock too, because locking a face clears it.
  const restoreRef = useRef<DesignerUiState | null>(initialUi?.lockedFace ? initialUi : null);
  const initialLockedFace = useRef(initialUi?.lockedFace ?? null).current;

  // Hosts remember one selected item (the first picked) across a reload.
  const primaryId = selectedIds[0] ?? null;
  useEffect(() => {
    if (restoreRef.current) return;
    onUiChange?.({ lockedFace, selectedSectionId: primaryId });
  }, [lockedFace, primaryId, onUiChange]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  // The ceiling's planes (a profiled ceiling) place its board layouts in 3D.
  // Keyed on the VALUES, not the objects: the host hands over a fresh `dims`
  // every render, and new planes would mean new layouts and a scene rebuild on
  // every render (which, among other things, cut short the camera's trip back
  // into the room on leaving edit mode).
  const profileKey = JSON.stringify(ceilingProfile ?? null);
  const planes = useMemo(
    () => ceilingGeometry({ length: dims.length, width: dims.width, height: dims.height, ceilingProfile }).planes,
    [dims.length, dims.width, dims.height, profileKey],
  );
  const layouts = useMemo(() => roomSurfacesToLayouts(room, planes), [room, planes]);
  // The other rooms' board layouts, worked out once per room and kept while its
  // surfaces are unchanged (an edit to the focus room touches none of them).
  const layoutCache = useRef(new WeakMap<RoomSurfaces, ReturnType<typeof roomSurfacesToLayouts>>());
  const contextRooms = useMemo<ContextRoom[] | undefined>(() => {
    if (!otherRooms?.length) return undefined;
    return otherRooms.map((other) => {
      let built = layoutCache.current.get(other.surfaces);
      if (!built) {
        built = roomSurfacesToLayouts(other.surfaces, ceilingGeometry({ ...other.dims, ceilingProfile: other.ceilingProfile }).planes);
        layoutCache.current.set(other.surfaces, built);
      }
      return {
        id: other.id, offsetX: other.offsetX, offsetZ: other.offsetZ,
        length: other.dims.length, width: other.dims.width, height: other.dims.height,
        layouts: built, framingOmit: other.framingOmit, props: other.props, accessories: other.accessories,
      };
    });
  }, [otherRooms]);
  const estimate = useMemo(() => hostEstimate ?? calculateFromSurfaces(room), [hostEstimate, room]);

  const lockedSurface = lockedFace ? surfaceForFace(room, lockedFace) : undefined;
  const selectedSections = useMemo(
    () => (lockedSurface ? lockedSurface.sections.filter((s) => selectedIds.includes(s.id)) : []),
    [lockedSurface, selectedIds],
  );
  const selectedOpenings = useMemo(
    () => (lockedSurface ? (lockedSurface.openings ?? []).filter((o) => selectedIds.includes(o.id)) : []),
    [lockedSurface, selectedIds],
  );
  const selectedCount = selectedSections.length + selectedOpenings.length;
  /** The one selected section / opening, when exactly one thing is selected — what the toolbar edits. */
  const soleSection = selectedCount === 1 ? selectedSections[0] ?? null : null;
  const soleOpening = selectedCount === 1 ? selectedOpenings[0] ?? null : null;
  /** The one selected SlatWall accessory (with its section), when that is all that is selected. */
  const soleAccessory = useMemo(() => {
    if (!lockedSurface || selectedIds.length !== 1 || selectedCount !== 0) return null;
    return findAccessory(lockedSurface, selectedIds[0]);
  }, [lockedSurface, selectedIds, selectedCount]);
  /** The SlatWall section the accessory palette adds to: the selected one, or the selected accessory's. */
  const paletteSection = soleSection?.productType === 'SlatWall' ? soleSection : soleAccessory?.section ?? null;
  /** Every selected accessory (with its section and surface rectangle), when only accessories are selected. */
  const selectedAccessories = useMemo(() => {
    if (!lockedSurface || selectedCount !== 0) return [];
    return selectedIds.flatMap((id) => {
      const found = findAccessory(lockedSurface, id);
      return found ? [{ ...found, rect: accessoryRect(found.section, found.accessory) }] : [];
    });
  }, [lockedSurface, selectedIds, selectedCount]);
  // Left / right walls are drawn mirrored: the surface's +x runs screen-left.
  const xFlipped = !!projection && projection.axisX.x < 0;
  // And a surface's +y can run screen-down.
  const yFlipped = !!projection && projection.axisY.y > 0;

  // A selection can outlive what it points at (undo of an add, redo of a
  // delete): keep only the ids still on the surface — sections, openings, and
  // the accessories hung on its SlatWall.
  useEffect(() => {
    if (!lockedSurface) return;
    const alive = selectedIds.filter((id) =>
      lockedSurface.sections.some((s) => s.id === id)
      || (lockedSurface.openings ?? []).some((o) => o.id === id)
      || !!findAccessory(lockedSurface, id));
    if (alive.length !== selectedIds.length) setSelectedIds(alive);
  }, [lockedSurface, selectedIds]);

  useEffect(() => {
    if (selectedIds.length === 0) setArrangeOpen(false);
  }, [selectedIds.length]);

  // The trim menu belongs to one selected item; any change of selection or surface closes it.
  useEffect(() => { setEdgeMenu(null); }, [selectedIds, lockedFace]);
  const closeEdgeMenu = useCallback(() => setEdgeMenu(null), []);
  // The "+" menu describes a bare area of one surface; it closes if the surface or its layout changes under it.
  useEffect(() => { setAddMenu(null); }, [lockedFace, room]);

  const select = useCallback((id: string | null, additive = false) => {
    setSelectedIds((cur) => {
      if (!id) return [];
      if (!additive) return [id];
      return cur.includes(id) ? cur.filter((other) => other !== id) : [...cur, id];
    });
  }, []);

  const isOpeningId = useCallback((id: string) => !!lockedSurface?.openings?.some((o) => o.id === id), [lockedSurface]);

  // ---------- Edits ----------
  /** Commit an edit if it was allowed; otherwise say why. Returns whether it went through. */
  const apply = useCallback((result: EditResult, coalesceKey?: string): boolean => {
    if (!result.ok) {
      setNotice(S.refused[result.reason] ?? result.reason);
      return false;
    }
    // A host may have a rule of its own (a floor plan's shared walls) and say no.
    const refusal = onCommit(result.room, coalesceKey);
    if (typeof refusal === 'string') {
      setNotice(S.refused[refusal] ?? refusal);
      return false;
    }
    setNotice(null);
    return true;
  }, [onCommit]);

  /** Move / resize one section or opening. */
  const commitRect = useCallback((id: string, rect: Rect, coalesceKey?: string) => {
    if (!lockedSurface) return;
    apply(
      isOpeningId(id)
        ? updateOpening(room, lockedSurface.id, id, rect, openingRules)
        : updateSection(room, lockedSurface.id, id, rect),
      coalesceKey,
    );
  }, [apply, room, lockedSurface, isOpeningId, openingRules]);

  const setRect = useCallback((patch: RectPatch, field: RectField) => {
    const target = soleSection ?? soleOpening;
    if (!target) return;
    commitRect(target.id, { ...rectOf(target), ...patch }, `${target.id}:${field}`);
  }, [commitRect, soleSection, soleOpening]);

  const setProductType = useCallback((type: SectionProductType) => {
    if (!lockedSurface || !soleSection) return;
    const color = pickColor(type, soleSection.color);
    if (!color) { setNotice(S.noProducts); return; }
    apply(changeProductType(room, lockedSurface.id, soleSection.id, type, color));
  }, [apply, room, lockedSurface, soleSection]);

  /**
   * Arrow keys: move what is selected one step in a SCREEN direction. The rules
   * decide how far it really goes (see nudgeItems); a run of nudges is one undo.
   */
  const nudgeSelected = useCallback((screenX: number, screenY: number, stepFt: number) => {
    if (!lockedSurface || selectedIds.length === 0) return;
    const dx = screenX * stepFt * (xFlipped ? -1 : 1);
    const dy = screenY * stepFt * (yFlipped ? -1 : 1);
    // A lone accessory: along its section, up or down a slot (a 3" pitch, whatever the step).
    if (selectedIds.length === 1 && findAccessory(lockedSurface, selectedIds[0])) {
      apply(nudgeAccessory(room, lockedSurface.id, selectedIds[0], dx, dy === 0 ? 0 : Math.sign(dy) * 0.25), `${selectedIds[0]}:nudge`);
      return;
    }
    apply(nudgeItems(room, lockedSurface.id, selectedIds, dx, dy, openingRules), `${selectedIds.join('+')}:nudge`);
  }, [apply, room, lockedSurface, selectedIds, xFlipped, yFlipped, openingRules]);

  const removeItems = useCallback((ids: string[]) => {
    if (!lockedSurface || ids.length === 0) return;
    // Accessories come off one by one; sections and openings go together.
    const accessoryIds = ids.filter((id) => findAccessory(lockedSurface, id));
    const rest = ids.filter((id) => !accessoryIds.includes(id));
    let current = room;
    for (const id of accessoryIds) {
      const result = deleteAccessory(current, lockedSurface.id, id);
      if (!result.ok) { apply(result); return; }
      current = result.room;
    }
    const result = rest.length ? deleteItems(current, lockedSurface.id, rest) : { ok: true as const, room: current };
    if (apply(result)) setSelectedIds([]);
  }, [apply, room, lockedSurface]);

  const duplicateSelected = useCallback(() => {
    if (!lockedSurface) return;
    if (soleSection) {
      const result = duplicateSection(room, lockedSurface.id, soleSection.id);
      if (apply(result) && result.ok) setSelectedIds([result.sectionId]);
    } else if (soleOpening) {
      const result = duplicateOpening(room, lockedSurface.id, soleOpening.id, openingRules);
      if (apply(result) && result.ok) setSelectedIds([result.openingId]);
    }
  }, [apply, room, lockedSurface, soleSection, soleOpening, openingRules]);

  /** One plain section filling the bare area — the surface's usual product, matching what is already there. */
  const addSingleAt = useCallback((gap: Rect) => {
    if (!lockedSurface) return;
    const type = getAvailableProductTypes(lockedSurface.kind)[0];
    // Match the surface's existing colour for that product where there is one.
    const neighbour = lockedSurface.sections.find((s) => s.productType === type);
    const color = type ? pickColor(type, neighbour?.color) : null;
    if (!type || !color) { setNotice(S.noProducts); return; }
    const section: Section = sectionForGap(lockedSurface, gap, type, color);
    if (neighbour?.orientation && type === 'WCB') section.orientation = neighbour.orientation;
    if (apply(addSection(room, lockedSurface.id, section))) setSelectedIds([section.id]);
  }, [apply, room, lockedSurface]);

  /**
   * The quick layouts offered for the bare area the "+" menu is open on: only
   * those whose products this retailer carries (and that this surface may
   * take), each marked whether its bands fit the area's height.
   */
  const addMenuChoices = useMemo<QuickLayoutChoice[]>(() => {
    if (!addMenu || !lockedSurface) return [];
    const types = getAvailableProductTypes(lockedSurface.kind);
    return QUICK_LAYOUTS
      .filter((layout) => layout.bands.every((b) => types.includes(b.productType)))
      .map((layout) => ({ layout, fits: layout.id === 'single' || quickLayoutHeights(layout.bands, quickLayoutLevelHeight(lockedSurface, addMenu.gap)) !== null }));
  }, [addMenu, lockedSurface]);

  const chooseQuickLayout = useCallback((id: QuickLayoutId) => {
    if (!addMenu || !lockedSurface) return;
    const { gap } = addMenu;
    setAddMenu(null);
    if (id === 'single') { addSingleAt(gap); return; }
    const layout = QUICK_LAYOUTS.find((l) => l.id === id);
    if (!layout) return;
    // The layout asks for white or gray; the retailer may carry only one of them.
    const sections = quickLayoutSections(lockedSurface, gap, layout, pickColor);
    if (!sections) { setNotice(S.quickLayoutTooSmall); return; }
    if (apply(addSections(room, lockedSurface.id, sections))) setSelectedIds(sections.map((s) => s.id));
  }, [addMenu, lockedSurface, addSingleAt, apply, room]);

  const closeAddMenu = useCallback(() => setAddMenu(null), []);

  // ---------- SlatWall accessories ----------
  /** Hang an accessory on the SlatWall the palette is open for, at the first free spot, and select it. */
  const addAccessoryOfKind = useCallback((kind: SlatWallAccessoryKind) => {
    if (!lockedSurface || !paletteSection) return;
    const result = addAccessory(room, lockedSurface.id, paletteSection.id, kind);
    if (!result.ok) { setNotice(result.reason === 'no-room' ? S.accessories.noRoom : S.refused[result.reason] ?? result.reason); return; }
    if (apply(result)) setSelectedIds([result.accessoryId]);
  }, [apply, room, lockedSurface, paletteSection]);

  /** An accessory was dragged to a new place on its section. */
  const commitAccessory = useCallback((id: string, x: number, y: number) => {
    if (!lockedSurface) return;
    apply(moveAccessory(room, lockedSurface.id, id, x, y));
  }, [apply, room, lockedSurface]);

  const addOpeningOfKind = useCallback((kind: OpeningKind) => {
    if (!lockedSurface) return;
    const added = addOpening(room, lockedSurface.id, kind, openingRules);
    if (!added.ok) { apply(added); return; }
    // A new window matches the windows already in the room (this wall's first):
    // nobody wants to restyle each one.
    const like = [lockedSurface, ...room.walls]
      .flatMap((s) => s.openings ?? [])
      .find((o) => o.kind === kind && o.style);
    const styled = like ? updateOpening(added.room, lockedSurface.id, added.openingId, { style: like.style }, openingRules) : added;
    if (apply(styled)) setSelectedIds([added.openingId]);
  }, [apply, room, lockedSurface, openingRules]);

  // ---------- Trim on an edge ----------
  const setEdgeTrim = useCallback((side: EdgeSide, value: string | undefined) => {
    const target = soleSection ?? soleOpening;
    if (!lockedSurface || !target) return;
    const edges = { ...(target.edges ?? {}) };
    if (value === undefined) delete edges[side];
    else edges[side] = value;
    const patch = { edges: Object.keys(edges).length ? edges : undefined };
    apply(soleSection
      ? updateSection(room, lockedSurface.id, target.id, patch)
      : updateOpening(room, lockedSurface.id, target.id, patch, openingRules));
    setEdgeMenu(null);
  }, [apply, room, lockedSurface, soleSection, soleOpening, openingRules]);

  /** What the trim menu needs for the edge it is open on. */
  const edgeMenuModel = useMemo(() => {
    const target = soleSection ?? soleOpening;
    if (!edgeMenu || !lockedSurface || !target || target.id !== edgeMenu.id) return null;
    // What the rules give this side with no override, stretch by stretch (a side
    // can meet more than one thing, and an opening takes its colour from each).
    const runs = (soleSection
      ? sectionTrims(lockedSurface, { ...soleSection, edges: undefined })
      : openingTrims(lockedSurface, { ...soleOpening!, edges: undefined })
    ).filter((r) => r.side === edgeMenu.side && r.neighbour.kind !== 'covered');
    const color: ProductColor = soleSection ? soleSection.color : runs.find((r) => r.neighbour.kind === 'section')?.color ?? 'White';
    const defaults = runs.map((r) => (r.trim ? findTrimProduct(r.trim, r.color)?.name ?? r.trim : S.edgeTrimNone));
    const options: EdgeTrimOption[] = [
      ...availableTrims(color).map((t) => ({ value: t.acronym, label: t.name })),
      { value: TRIM_NONE, label: S.edgeTrimNone },
    ];
    return {
      defaultLabel: [...new Set(defaults)].join(' / ') || S.edgeTrimNone,
      current: target.edges?.[edgeMenu.side],
      options,
    };
  }, [edgeMenu, lockedSurface, soleSection, soleOpening]);

  // ---------- Align / distribute ----------
  // The popover speaks in screen directions; the surface's own coordinates run
  // bottom-left, and mirrored on the left / right walls.
  // Accessories arrange among themselves, within their SlatWall; sections and openings within the surface.
  const arrangingAccessories = selectedAccessories.length > 0;
  const arrangeItems = useMemo(
    () => (arrangingAccessories
      ? selectedAccessories.map((a) => ({ id: a.accessory.id, rect: a.rect }))
      : [...selectedSections, ...selectedOpenings].map((s) => ({ id: s.id, rect: rectOf(s) }))),
    [arrangingAccessories, selectedAccessories, selectedSections, selectedOpenings],
  );

  const arrangeFrame = useCallback((target: ArrangeSettings['target']): Rect | null => {
    if (!lockedSurface || arrangeItems.length === 0) return null;
    if (target === 'selection') return boundsOf(arrangeItems.map((i) => i.rect));
    // "The whole": the surface — or, for accessories, the SlatWall they are on
    // (several sections' worth, if the selection spans more than one).
    if (arrangingAccessories) return boundsOf([...new Set(selectedAccessories.map((a) => a.section))].map(rectOf));
    return { x: 0, y: 0, width: lockedSurface.width, height: lockedSurface.height };
  }, [lockedSurface, arrangeItems, arrangingAccessories, selectedAccessories]);

  const applyArrange = useCallback((patches: RectPatches) => {
    if (!lockedSurface) return;
    apply(arrangingAccessories
      ? arrangeAccessories(room, lockedSurface.id, patches)
      : updateItems(room, lockedSurface.id, patches, openingRules));
  }, [apply, room, lockedSurface, arrangingAccessories, openingRules]);

  const alignSelected = useCallback((mode: AlignMode, target: ArrangeSettings['target']) => {
    const frame = arrangeFrame(target);
    if (!lockedSurface || !frame) return;
    const surfaceMode: AlignMode = xFlipped && mode === 'left' ? 'right' : xFlipped && mode === 'right' ? 'left' : mode;
    applyArrange(alignPatches(arrangeItems, surfaceMode, frame));
  }, [lockedSurface, arrangeFrame, arrangeItems, xFlipped, applyArrange]);

  const distributeSelected = useCallback((axis: 'x' | 'y', target: ArrangeSettings['target'], spacing: DistributeSpacing) => {
    const frame = arrangeFrame(target);
    if (!lockedSurface || !frame) return;
    const patches = distributePatches(arrangeItems, axis, frame, spacing);
    if (!patches) { setNotice(S.noRoomToDistribute); return; }
    applyArrange(patches);
  }, [lockedSurface, arrangeFrame, arrangeItems, applyArrange]);

  // Another document opened in place: nothing of the last room's editing carries over.
  const documentKeyRef = useRef(documentKey);
  useEffect(() => {
    if (documentKeyRef.current === documentKey) return;
    documentKeyRef.current = documentKey;
    setSelectedIds([]);
    setArrangeOpen(false);
    setEdgeMenu(null);
    setAddMenu(null);
    setSurfaceMenuOpen(false);
    setNotice(null);
    if (lockedFace) viewerApi.current?.exitEditMode();
  }, [documentKey]); // the key alone: this runs for a new document, not for what the last one did

  // ---------- Lock / selection ----------
  const handleFaceLock = useCallback((face: Face | null) => {
    setLockedFace(face);
    const restore = restoreRef.current;
    restoreRef.current = null;
    setSelectedIds(restore && restore.lockedFace === face && restore.selectedSectionId ? [restore.selectedSectionId] : []);
    setNotice(null);
    // The effect above skipped its report while the restore was pending; if the
    // restore came back empty (that face no longer exists) nothing else will
    // change to trigger it, so report here.
    if (restore && !face) onUiChange?.({ lockedFace: null, selectedSectionId: null });
    // Belt-and-suspenders: drop the projection too on exit. The viewer also
    // emits null via onProjectionChange, but clearing here prevents any stale
    // overlay frame from surviving a re-lock transition.
    if (!face) setProjection(null);
  }, [onUiChange]);

  const handleFramingToggle = useCallback((visible: boolean) => setFramingHidden(!visible), []);

  const faces = useMemo(() => faceOrder(room).filter((f) => surfaceForFace(room, f)), [room]);
  const stepSurface = useCallback((delta: 1 | -1) => {
    if (!lockedFace || faces.length < 2) return;
    const i = faces.indexOf(lockedFace);
    viewerApi.current?.lockToFace(faces[(i + delta + faces.length) % faces.length]);
  }, [lockedFace, faces]);

  // ---------- Keyboard ----------
  // Escape backs out one level at a time: trim menu → align panel → selection →
  // lock → close. (A drag in progress takes Escape for itself, in the overlay.)
  // Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y undo and redo; Ctrl+D duplicates; Delete
  // removes what is selected; the arrow keys nudge it (6", Ctrl 1", Shift 1').
  // None of them fire while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      if (e.key === 'Escape') {
        if (typing) { el?.blur(); return; }
        if (surfaceMenuOpen) setSurfaceMenuOpen(false);
        else if (addMenu) setAddMenu(null);
        else if (edgeMenu) setEdgeMenu(null);
        else if (arrangeOpen) setArrangeOpen(false);
        else if (selectedIds.length) setSelectedIds([]);
        else if (lockedFace) viewerApi.current?.exitEditMode();
        else onClose?.();
        return;
      }
      if (typing) return;
      if (mod && key === 'z') { e.preventDefault(); if (e.shiftKey) onRedo?.(); else onUndo?.(); return; }
      if (mod && key === 'y') { e.preventDefault(); onRedo?.(); return; }
      if (mod && key === 'd' && selectedIds.length === 1) { e.preventDefault(); duplicateSelected(); return; }
      const arrow = NUDGE_ARROWS[e.key];
      if (arrow && selectedIds.length && lockedFace) {
        e.preventDefault(); // or the page scrolls
        nudgeSelected(arrow[0], arrow[1], mod ? NUDGE_FINE_FT : e.shiftKey ? NUDGE_COARSE_FT : NUDGE_FT);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length) {
        e.preventDefault();
        removeItems(selectedIds);
      }
    };
    if (suspended) return; // the planner is open over this: its keys, not ours
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIds, arrangeOpen, edgeMenu, addMenu, surfaceMenuOpen, lockedFace, onClose, onUndo, onRedo, removeItems, duplicateSelected, nudgeSelected, suspended]);

  const bom = estimate.billOfMaterials;
  const trimItems = [...(bom.jTrimsSnapIn ?? []), ...bom.hTrims, ...(bom.otherTrims ?? [])];
  const lines = [...bom.wallAndCeilingBoard, ...bom.slatwall, ...trimItems];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#1a1a1e' }}>
      {/* The two-row header (chrome/DesignerHeader): the brand, then the
          controls — undo/redo, the host's, the render controls — which drop to
          icons when the row is short of room. */}
      <DesignerHeader
        title={title}
        brand={brand}
        end={(headerEnd || onClose) ? (
          <>
            {headerEnd}
            {onClose && <IconTextButton quiet iconOnly icon={<X size={18} />} label={S.close} onClick={onClose} />}
          </>
        ) : undefined}
      >
        {(onUndo || onRedo) && (
          <>
            <IconTextButton icon={<Undo2 size={15} />} label={S.undoLabel} title={S.undo} disabled={!canUndo} onClick={onUndo} />
            <IconTextButton icon={<Redo2 size={15} />} label={S.redoLabel} title={S.redo} disabled={!canRedo} onClick={onRedo} />
          </>
        )}
        {headerSlot}
        {/* How the view is lit and drawn, and the Photoreal render window with
            its Render button. Not on phones: no room, and nothing to hover. */}
        {!phone && (
          <RenderControls
            roomSize={{ length: dims.length, width: dims.width }}
            staged={finish}
            onRender={async (photo) => {
              // The room as built, with its camera and lamps — and how Blender is
              // to render it (manifest.render) — for the renderer (scripts/blender).
              // The 360 opens on the wall the space's own props or its garage door say (panorama.ts).
              const out = await viewerApi.current?.exportScene({ panoramaFace: panoramaFace(spaceType, room, props) });
              if (!out) return S.exportRenderNotReady;
              const manifest = { ...out.manifest, render: applyFinish(photo, finish) };
              for (const file of renderExportFiles(out.glb, manifest)) {
                const url = URL.createObjectURL(file.blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = file.name;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 2000);
              }
              return undefined;
            }}
          />
        )}
      </DesignerHeader>

      <div style={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
        <Room3DViewer
          length={dims.length}
          width={dims.width}
          height={dims.height}
          showBoards
          showCeiling
          showFraming
          fillParent
          enhancedRendering
          editable
          editInsetTop={paletteSection ? EDIT_INSET_TOP + PALETTE_INSET : EDIT_INSET_TOP}
          editInsetBottom={EDIT_INSET_BOTTOM}
          prebuiltLayouts={layouts}
          contextRooms={contextRooms}
          framingOmit={framingOmit}
          props={props}
          yard={yard}
          heldOpen={heldOpen}
          accessories={accessoryViews}
          roomKey={roomKey}
          roomOrigin={roomOrigin}
          onRoomFocus={onFocusRoom}
          onFloorDoubleClick={onOpenPlanner}
          initialLockedFace={initialLockedFace}
          apiRef={viewerApi}
          onFaceLock={handleFaceLock}
          onSectionPick={select}
          onProjectionChange={setProjection}
          onFramingToggle={handleFramingToggle}
          suspended={suspended}
          // Suspended (the planner over it): no edit overlay — its key
          // listeners and menus would answer the planner's keys and clicks.
          overlay={suspended ? null : (
            <>
              {/* The logo, under everything else. Told what already sits in each
                  corner, so it steps clear of it. */}
              <ViewLogo
                clear={{
                  'top-left': lockedSurface ? (lockedSurface.kind === 'wall' ? 100 : 56) : 0,
                  'top-right': lockedSurface ? 40 : 0,
                  'bottom-left': lockedSurface && (soleSection || soleOpening) ? 40 : 0,
                }}
              />
              {projection && lockedSurface && (
                <SectionEditOverlay
                  surface={lockedSurface}
                  selectedIds={selectedIds}
                  projection={projection}
                  openingRules={openingRules}
                  onSelect={select}
                  onCommitRect={commitRect}
                  onDelete={(id) => removeItems([id])}
                  onAddAt={(gap, at) => { setEdgeMenu(null); setAddMenu({ gap, ...at }); }}
                  onEdgeClick={(id, side, at) => setEdgeMenu({ id, side, ...at })}
                  onCommitAccessory={commitAccessory}
                  // The ceiling has no framing to show its edges, so always outline it.
                  framingHidden={framingHidden || isCeilingFace(lockedFace)}
                />
              )}

              {/* Which surface is locked, stepping to the next one, and adding
                  windows and doors to it (walls only). */}
              {lockedSurface && (
                <div
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  style={{ ...FLOATING_PANEL, left: 14, top: 12, display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 8px', borderRadius: 10 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {faces.length > 1 && (
                      <button type="button" title={S.prevSurface} aria-label={S.prevSurface} onClick={() => stepSurface(-1)} style={headerButton(true)}><ChevronLeft size={16} /></button>
                    )}
                    {/* The name is the way to any other surface: a quiet menu, not a boxed select. */}
                    <div style={{ position: 'relative', flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
                      <button
                        type="button"
                        className="designer-surface-title"
                        title={S.chooseSurface}
                        aria-label={S.chooseSurface}
                        aria-haspopup="menu"
                        aria-expanded={surfaceMenuOpen}
                        onClick={() => setSurfaceMenuOpen((o) => !o)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px', background: 'none', border: 'none', borderRadius: 5, color: '#fff', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}
                      >
                        {lockedSurface.name}
                        <ChevronDown size={12} style={{ opacity: 0.55 }} />
                      </button>
                      {surfaceMenuOpen && (
                        <div
                          role="menu"
                          aria-label={S.chooseSurface}
                          style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 6, minWidth: 170, zIndex: 8, background: 'rgba(26, 26, 30, 0.97)', color: '#fff', borderRadius: 10, padding: 6, border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 10px 28px rgba(0,0,0,0.45)' }}
                        >
                          {faces.map((face) => {
                            const active = face === lockedFace;
                            return (
                              <button
                                key={face}
                                type="button"
                                role="menuitemradio"
                                aria-checked={active}
                                onClick={() => { setSurfaceMenuOpen(false); if (!active) viewerApi.current?.lockToFace(face); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#fff', background: active ? 'rgba(254,177,0,0.18)' : 'transparent', border: active ? '1px solid #FEB100' : '1px solid transparent', whiteSpace: 'nowrap' }}
                              >
                                <span style={{ width: 8, height: 8, borderRadius: 4, flex: '0 0 auto', background: active ? '#FEB100' : 'rgba(255,255,255,0.25)' }} />
                                {surfaceForFace(room, face)?.name ?? face}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {faces.length > 1 && (
                      <button type="button" title={S.nextSurface} aria-label={S.nextSurface} onClick={() => stepSurface(1)} style={headerButton(true)}><ChevronRight size={16} /></button>
                    )}
                  </div>
                  {lockedSurface.kind === 'wall' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                      <span style={{ fontSize: 11, color: '#b9b9c4', paddingRight: 2 }}>{S.addOpening}</span>
                      {ADD_OPENING.map(({ kind, Icon }) => (
                        <button
                          key={kind}
                          type="button"
                          title={S.addOpeningOf(S.openingName[kind])}
                          aria-label={S.addOpeningOf(S.openingName[kind])}
                          onClick={() => addOpeningOfKind(kind)}
                          style={{ ...headerButton(true), width: 34, height: 30 }}
                        >
                          <Icon size={16} />
                        </button>
                      ))}
                      {room.walls.length > 1 && (
                        <>
                          <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)', margin: '0 2px' }} />
                          <CopyLayoutPopover
                            source={lockedSurface}
                            others={room.walls.filter((w) => w.id !== lockedSurface.id)}
                            onCopy={(ids, openings) => apply(copyLayout(room, lockedSurface.id, ids, { openings, margin: openingRules.margin }))}
                            buttonStyle={headerButton(true)}
                            panelStyle={FLOATING_PANEL}
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* The selection's controls, floating centred over the top of the view.
                  The viewer keeps the locked surface clear of this band
                  (`editInsetTop`), so the panel never sits on the wall's top edge.
                  One thing selected → its full toolbar; several → what applies
                  to a group (arrange, delete). */}
              {lockedSurface && (selectedCount > 0 || selectedAccessories.length > 0) && (() => {
                const arrange = (
                  <ArrangeControl
                    open={arrangeOpen}
                    onToggle={() => setArrangeOpen((o) => !o)}
                    count={selectedCount || selectedAccessories.length}
                    surfaceKind={lockedSurface.kind}
                    frameLabel={arrangingAccessories ? S.arrangeSlatWall : undefined}
                    settings={arrangeSettings}
                    onSettings={setArrangeSettings}
                    onAlign={alignSelected}
                    onDistribute={distributeSelected}
                  />
                );
                return (
                  // A centred column: the toolbar, and under it (a gap apart)
                  // the accessory palette when a SlatWall is in hand.
                  <div
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: 'absolute', zIndex: 4, top: 12, left: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, pointerEvents: 'none' }}
                  >
                  <div style={{ ...FLOATING_PANEL, position: 'relative', width: 'fit-content', maxWidth: 'calc(100vw - 28px)', padding: '10px 14px', borderRadius: 12, pointerEvents: 'auto' }}>
                    {soleSection ? (
                      <SectionToolbar
                        section={soleSection}
                        surface={lockedSurface}
                        xFlipped={xFlipped}
                        onProductType={setProductType}
                        onColor={(color) => apply(updateSection(room, lockedSurface.id, soleSection.id, { color }))}
                        onOrientation={(orientation) => apply(updateSection(room, lockedSurface.id, soleSection.id, { orientation }))}
                        onRect={setRect}
                        onDuplicate={duplicateSelected}
                        onDelete={() => removeItems([soleSection.id])}
                        actions={arrange}
                      />
                    ) : soleOpening ? (
                      <OpeningToolbar
                        opening={soleOpening}
                        surface={lockedSurface}
                        xFlipped={xFlipped}
                        margin={openingRules.margin}
                        onRect={setRect}
                        onStyle={(style) => apply(updateOpening(room, lockedSurface.id, soleOpening.id, { style }, openingRules))}
                        onFloorPinned={(pinned) => apply(setOpeningFloorPinned(room, lockedSurface.id, soleOpening.id, pinned, openingRules))}
                        onHanding={(patch) => apply(updateOpening(room, lockedSurface.id, soleOpening.id, patch, openingRules))}
                        onDuplicate={duplicateSelected}
                        onDelete={() => removeItems([soleOpening.id])}
                        actions={arrange}
                      />
                    ) : soleAccessory ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{accessoryDef(soleAccessory.accessory.kind).label}</span>
                        <span style={{ fontSize: 11, color: '#b9b9c4', whiteSpace: 'nowrap' }}>{accessoryDef(soleAccessory.accessory.kind).sku}</span>
                        <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.18)' }} />
                        <button
                          type="button"
                          title={S.accessories.deleteOne}
                          aria-label={S.accessories.deleteOne}
                          onClick={() => removeItems([soleAccessory.accessory.id])}
                          style={{ ...headerButton(true), width: 30, height: 30, borderRadius: 7, border: '2px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.06)' }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{arrangingAccessories ? S.selectedAccessories(selectedAccessories.length) : S.selectedCount(selectedCount)}</span>
                        <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.18)' }} />
                        {arrange}
                        <button
                          type="button"
                          title={S.deleteSections}
                          aria-label={S.deleteSections}
                          onClick={() => removeItems(selectedIds)}
                          style={{ ...headerButton(true), width: 30, height: 30, borderRadius: 7, border: '2px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.06)' }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Hooks, baskets and shelves for the selected SlatWall: a click hangs one. */}
                  {paletteSection && (
                    <div style={{ ...FLOATING_PANEL, position: 'relative', width: 'fit-content', padding: '10px 12px', borderRadius: 12, pointerEvents: 'auto' }}>
                      <AccessoryPalette onAdd={addAccessoryOfKind} />
                    </div>
                  )}
                  </div>
                );
              })()}

              {addMenu && lockedSurface && (
                <QuickLayoutMenu
                  x={addMenu.x}
                  y={addMenu.y}
                  choices={addMenuChoices}
                  onChoose={chooseQuickLayout}
                  onClose={closeAddMenu}
                />
              )}

              {edgeMenu && edgeMenuModel && (
                <EdgeTrimMenu
                  side={edgeMenu.side}
                  x={edgeMenu.x + 10}
                  y={edgeMenu.y + 10}
                  defaultLabel={edgeMenuModel.defaultLabel}
                  current={edgeMenuModel.current}
                  options={edgeMenuModel.options}
                  onChoose={(value) => setEdgeTrim(edgeMenu.side, value)}
                  onClose={closeEdgeMenu}
                />
              )}

              {/* Key to the edge colours drawn on a lone selection (not on a phone: no room for it). */}
              {!phone && lockedSurface && (soleSection || soleOpening) && (
                <div style={{ ...FLOATING_PANEL, left: 14, bottom: 14, pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, fontSize: 11 }}>
                  <span style={{ color: '#b9b9c4' }}>{S.edgeKey}</span>
                  {([['perimeter', S.edgeKeyPerimeter], ['section', S.edgeKeySection], ['opening', S.edgeKeyOpening], ['bare', S.edgeKeyBare]] as const).map(([kind, text]) => (
                    <span key={kind} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 16, height: 4, borderRadius: 2, background: EDGE_COLORS[kind] }} />
                      {text}
                    </span>
                  ))}
                </div>
              )}

              {/* Why the last edit was refused. One fixed place — bottom centre of the
                  view — so it is never hidden behind the align panel and never
                  changes the size of the toolbar it relates to. */}
              {lockedSurface && notice && (
                <div
                  role="status"
                  style={{ ...FLOATING_PANEL, bottom: 16, left: 0, right: 0, margin: '0 auto', width: 'fit-content', maxWidth: 'calc(100% - 28px)', zIndex: 7, pointerEvents: 'none', background: 'rgba(60, 20, 16, 0.95)', border: '1px solid rgba(255,140,120,0.45)', color: '#ffd2c9', fontSize: 13, fontWeight: 600, borderRadius: 10, padding: '9px 14px' }}
                >
                  {notice}
                </div>
              )}
            </>
          )}
        />
      </div>

      {/* The live readout of materials. Not on a phone: the room needs the height. */}
      {!phone && <div style={{ flex: '0 0 auto', padding: '0.4rem 1rem', background: 'rgba(0,0,0,0.5)', color: '#cfcfd6', fontSize: '0.8rem', textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: 18, justifyContent: 'center', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span>{S.readoutWcb}: <strong style={{ color: '#fff' }}>{totalQty(bom.wallAndCeilingBoard)}</strong></span>
          <span>{S.readoutSlatWall}: <strong style={{ color: '#fff' }}>{totalQty(bom.slatwall)}</strong></span>
          <span>{S.readoutTrim}: <strong style={{ color: '#fff' }}>{totalQty(trimItems)}</strong></span>
          <span>{S.readoutScrews}: <strong style={{ color: '#fff' }}>{bom.screws.total}</strong></span>
          <span>{S.readoutCost}: <strong style={{ color: '#FEB100' }}>${estimate.totals.totalCostUSD.toFixed(0)}</strong> USD</span>
          {accessories && accessories.length > 0 && (
            <span title={accessories.map((a) => `${a.quantity} × ${a.label} (${a.sku})`).join('\n')}>
              {S.readoutAccessories}: <strong style={{ color: '#fff' }}>{accessories.reduce((n, a) => n + a.quantity, 0)}</strong>
            </span>
          )}
          {estimate.issues.length > 0 && (
            <span style={{ color: '#ffb4a8', fontWeight: 600 }}>{S.layoutIssues(estimate.issues.length)}</span>
          )}
        </div>
        <div style={{ fontSize: '0.72rem', opacity: 0.8, marginTop: 2 }}>
          {lines.map((item) => `${item.quantity} × ${item.name}`).join('   ·   ')}
        </div>
      </div>}
    </div>
  );
};

export default DesignerWorkspace;
